import React, { useState } from 'react';
import { Mail, Lock, AlertCircle, ArrowRight, X, Send, MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trialExpired, setTrialExpired] = useState(false);

  // État pour la modale de support
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState(false);
  const [supportError, setSupportError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setTrialExpired(false);

    console.log('🔐 Tentative de connexion avec:', {
      email: formData.email,
      password: formData.password ? '***' : 'vide',
      apiUrl: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/users/login`
    });

    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      setIsLoading(false);
      return;
    }

    try {
      console.log('📡 Appel de la fonction login...');
      const success = await login(formData.email, formData.password);
      console.log('✅ Résultat de la connexion:', success);

      console.log('🎉 Connexion réussie, redirection vers dashboard...');
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('💥 Erreur lors de la connexion:', error);

      if (error?.response?.status === 403 || error?.response?.data?.error === 'trial_expired') {
        console.log('⏰ Période d\'essai expirée détectée');
        setTrialExpired(true);
        setError(error.response.data.message || 'Votre période d\'essai est terminée.');
      } else if (error?.response?.status === 401) {
        console.log('🔒 Erreur d\'authentification (401)');
        setError('Email ou mot de passe incorrect');
      } else {
        console.log('❌ Autre erreur:', error?.response?.status);
        setError('Erreur de connexion au serveur');
      }
    }

    setIsLoading(false);
  };

  // Handlers pour la modale de support
  const handleSupportInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({ ...prev, [name]: value }));
    setSupportError(null);
  };

  const handleOpenSupportModal = () => {
    setShowSupportModal(true);
    setSupportSuccess(false);
    setSupportError(null);
    setSupportForm({ name: '', email: '', subject: '', message: '' });
  };

  const handleCloseSupportModal = () => {
    setShowSupportModal(false);
    setSupportSuccess(false);
    setSupportError(null);
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSendingSupport(true);
    setSupportError(null);

    // Validation des champs
    if (!supportForm.name || !supportForm.email || !supportForm.subject || !supportForm.message) {
      setSupportError('Veuillez remplir tous les champs');
      setIsSendingSupport(false);
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(supportForm.email)) {
      setSupportError('Veuillez entrer une adresse email valide');
      setIsSendingSupport(false);
      return;
    }

    try {
      const response = await axios.post(
        'https://app.logiciel-redimmo.com/api/support/contact',
        {
          name: supportForm.name,
          email: supportForm.email,
          subject: supportForm.subject,
          message: supportForm.message,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setSupportSuccess(true);
        setSupportForm({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          handleCloseSupportModal();
        }, 3000);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setSupportError(
        error.response?.data?.error || 
        'Erreur lors de l\'envoi du message. Veuillez réessayer.'
      );
    } finally {
      setIsSendingSupport(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-white via-slate-50 to-slate-100 relative overflow-hidden">
      {/* Éléments de décoration subtils */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-green-50/50 rounded-full blur-3xl -mr-32 -mt-32"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-50/30 rounded-full blur-3xl -ml-32 -mb-32"></div>
      
      <div className="w-full max-w-md px-6 relative z-10">
        {/* Header avec logo et titre */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-8 p-6 bg-gradient-to-br from-green-50 to-slate-50 rounded-2xl shadow-sm">
            <img 
              src="https://image.noelshack.com/fichiers/2025/08/1/1739816442-red-immo-3.png" 
              alt="RED IMMO Logo" 
              className="h-36 w-36 object-contain"
            />
          </div>
          <p className="text-slate-600 text-sm font-medium">
            Connectez-vous à votre espace agence
          </p>
        </div>

        {/* Carte de connexion */}
        <div className="relative">
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
            {/* Ligne d'accent en haut */}
            <div className="h-1.5 bg-gradient-to-r from-green-500 via-green-400 to-green-600"></div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Message d'erreur */}
              {error && (
                <div className={`rounded-xl p-4 border ${
                  trialExpired
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex gap-3">
                    <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      trialExpired ? 'text-amber-600' : 'text-red-600'
                    }`} />
                    <div className="flex-1">
                      <h3 className={`text-sm font-semibold mb-1 ${
                        trialExpired
                          ? 'text-amber-900'
                          : 'text-red-900'
                      }`}>
                        {trialExpired ? 'Période d\'essai expirée' : 'Erreur de connexion'}
                      </h3>
                      <p className={`text-sm ${
                        trialExpired
                          ? 'text-amber-800'
                          : 'text-red-800'
                      }`}>
                        {error}
                      </p>
                      {trialExpired && (
                        <div className="mt-4 pt-4 border-t border-amber-200">
                          <p className="text-xs text-amber-700 mb-3 font-medium">
                            Contactez notre équipe pour activer votre abonnement :
                          </p>
                          <a
                            href="tel:+33625765202"
                            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold text-sm transition-colors"
                          >
                            <span>+33 6 25 76 52 02</span>
                            <ArrowRight className="h-4 w-4" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Champ Email */}
              <div className="space-y-2.5">
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700">
                  Adresse email
                </label>
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-500/20 transition-all duration-200">
                  <Mail className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="flex-1 bg-transparent text-slate-900 placeholder-slate-400 focus:outline-none"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              {/* Champ Mot de passe */}
              <div className="space-y-2.5">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                  Mot de passe
                </label>
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-500/20 transition-all duration-200">
                  <Lock className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="flex-1 bg-transparent text-slate-900 placeholder-slate-400 focus:outline-none"
                    placeholder="Votre mot de passe"
                  />
                </div>
              </div>

              {/* Bouton de connexion */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-8 py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-100 disabled:scale-100 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:shadow-green-500/20 flex items-center justify-center gap-2 group"
              >
                <span>{isLoading ? 'Connexion en cours...' : 'Se connecter'}</span>
                {!isLoading && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Besoin d'aide ?{' '}
            <button
              onClick={handleOpenSupportModal}
              className="text-green-600 hover:text-green-700 font-medium cursor-pointer transition-colors underline decoration-dotted underline-offset-2"
            >
              Contactez le support
            </button>
          </p>
        </div>
      </div>

      {/* Modale de contact support */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header de la modale */}
            <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-6 w-6 text-white" />
                <h2 className="text-xl font-semibold text-white">Contacter le support</h2>
              </div>
              <button
                onClick={handleCloseSupportModal}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Contenu de la modale */}
            <div className="p-6">
              {supportSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <Send className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Message envoyé avec succès</h3>
                  <p className="text-slate-600 text-sm">
                    Notre équipe vous répondra dans les plus brefs délais à l'adresse email fournie.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSupportSubmit} className="space-y-5">
                  {/* Message d'erreur */}
                  {supportError && (
                    <div className="rounded-lg p-4 bg-red-50 border border-red-200">
                      <div className="flex gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-800">{supportError}</p>
                      </div>
                    </div>
                  )}

                  {/* Champ Nom */}
                  <div className="space-y-2">
                    <label htmlFor="support-name" className="block text-sm font-semibold text-slate-700">
                      Nom complet
                    </label>
                    <input
                      id="support-name"
                      name="name"
                      type="text"
                      required
                      value={supportForm.name}
                      onChange={handleSupportInputChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 text-slate-900 placeholder-slate-400"
                      placeholder="Votre nom complet"
                    />
                  </div>

                  {/* Champ Email */}
                  <div className="space-y-2">
                    <label htmlFor="support-email" className="block text-sm font-semibold text-slate-700">
                      Adresse email
                    </label>
                    <input
                      id="support-email"
                      name="email"
                      type="email"
                      required
                      value={supportForm.email}
                      onChange={handleSupportInputChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 text-slate-900 placeholder-slate-400"
                      placeholder="votre@email.com"
                    />
                  </div>

                  {/* Champ Sujet */}
                  <div className="space-y-2">
                    <label htmlFor="support-subject" className="block text-sm font-semibold text-slate-700">
                      Sujet
                    </label>
                    <input
                      id="support-subject"
                      name="subject"
                      type="text"
                      required
                      value={supportForm.subject}
                      onChange={handleSupportInputChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 text-slate-900 placeholder-slate-400"
                      placeholder="Ex: Problème de connexion"
                    />
                  </div>

                  {/* Champ Message */}
                  <div className="space-y-2">
                    <label htmlFor="support-message" className="block text-sm font-semibold text-slate-700">
                      Message
                    </label>
                    <textarea
                      id="support-message"
                      name="message"
                      required
                      rows={5}
                      value={supportForm.message}
                      onChange={handleSupportInputChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 text-slate-900 placeholder-slate-400 resize-none"
                      placeholder="Décrivez votre problème ou votre question en détail..."
                    />
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseSupportModal}
                      className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingSupport}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                    >
                      {isSendingSupport ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Envoi...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          <span>Envoyer</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
